import type {
  DataTableProps,
  DataTableValueArray,
  DataTableSelectionMultipleChangeEvent,
  DataTableSelectionSingleChangeEvent,
} from "primereact/datatable";
import { classNames as joinClassNames } from "primereact/utils";
import {
  Children,
  Fragment,
  isValidElement,
  useId,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import type { KeyboardEvent, ReactElement, ReactNode } from "react";

import { callAll } from "../../utils/call-all";
import { CLICKABLE_ROW_CLASS, clickableRow } from "../../utils/clickable-row";
import { LOADING_LABEL } from "../state-message";
import {
  COLUMN_PART,
  EMPTY_PART,
  ERROR_PART,
  FORBIDDEN_PART,
  LOADING_PART,
} from "./hub-table.constants";
import styles from "./hub-table.module.scss";
import { useHeaderHeight } from "./use-header-height";
import type {
  HubTableColumnProps,
  HubTableRootAttributes,
  HubTableScrollFixed,
  HubTableSlots,
  HubTableView,
  HubTableViewModel,
  UseHubTableOptions,
} from "./types-hub-table";

/** Área que rola dentro do `DataTable` do Prime. */
const SCROLLER_SELECTOR = ".p-datatable-wrapper";
/** Cabeçalho do `DataTable` (sticky com `scrollable`) e as células das colunas congeladas (`frozen`). */
const HEADER_SELECTOR = ".p-datatable-thead";
const FROZEN_CLASS = "p-frozen-column";

const NO_FIXED: HubTableScrollFixed = { header: 0, frozenStart: 0, frozenEnd: 0 };

/** O que fica parado na área que rola: a altura do cabeçalho e as larguras das colunas congeladas em cada ponta. */
function measureFixed(scroller: HTMLElement) {
  const header = scroller.querySelector<HTMLElement>(HEADER_SELECTOR);
  const cells = [...(header?.querySelector("tr")?.children ?? [])];
  const leading = (row: Element[]) => {
    const firstScrolling = row.findIndex((cell) => !cell.classList.contains(FROZEN_CLASS));
    return firstScrolling === -1 ? row : row.slice(0, firstScrolling);
  };
  const start = leading(cells);
  // Todas congeladas: nada rola, e a ponta final não conta de novo as mesmas colunas.
  const end = start.length === cells.length ? [] : leading([...cells].reverse());
  const width = (run: Element[]) =>
    run.reduce((total, cell) => total + cell.getBoundingClientRect().width, 0);
  return {
    header,
    headerHeight: header?.getBoundingClientRect().height ?? 0,
    frozenCells: [...start, ...end],
    start: width(start),
    end: width(end),
  };
}

const isPart = (child: ReactNode, name: string): child is ReactElement =>
  isValidElement(child) &&
  typeof child.type !== "string" &&
  (child.type as { displayName?: string }).displayName === name;

/** `Children.toArray` não abre fragmentos: as colunas e os estados podem vir dentro de um `<>…</>`. */
function flattenChildren(children: ReactNode): ReactNode[] {
  return Children.toArray(children).flatMap((child) =>
    isValidElement<{ children?: ReactNode }>(child) && child.type === Fragment
      ? flattenChildren(child.props.children)
      : [child],
  );
}

const SAME_CONTENT_MAX_DEPTH = 4;

/**
 * Igualdade do `Empty` próprio entre renders, sem contar funções (recriadas a cada render). Só desce em elementos
 * React, arrays e objetos planos, até `SAME_CONTENT_MAX_DEPTH`; `Date` vale pelo instante e qualquer outra coisa
 * (Map, instância de classe) pela identidade, então o limite de profundidade conta como "mudou".
 */
function sameContent(a: unknown, b: unknown, depth = 0): boolean {
  if (typeof a === "function" && typeof b === "function") return true;
  if (Object.is(a, b)) return true;
  if (a instanceof Date && b instanceof Date) return a.getTime() === b.getTime();
  if (depth >= SAME_CONTENT_MAX_DEPTH) return false;
  if (isValidElement(a) && isValidElement(b)) {
    return a.type === b.type && a.key === b.key && sameContent(a.props, b.props, depth + 1);
  }
  const isArrays = Array.isArray(a) && Array.isArray(b);
  if (!isArrays && !(isPlainObject(a) && isPlainObject(b))) return false;
  const left = a as Record<string, unknown>;
  const right = b as Record<string, unknown>;
  const keys = new Set([...Object.keys(left), ...Object.keys(right)]);
  return [...keys].every((key) => sameContent(left[key], right[key], depth + 1));
}

function isPlainObject(value: unknown): value is Record<string, unknown> {
  if (typeof value !== "object" || value === null || isValidElement(value)) return false;
  const proto = Object.getPrototypeOf(value) as unknown;
  return proto === Object.prototype || proto === null;
}

/** Separa os filhos do Root: colunas, e os estados `Empty`, `Loading`, `Error` e `Forbidden`; o resto é ignorado. */
function splitChildren(children: ReactNode): HubTableSlots {
  const parts = flattenChildren(children);
  const slot = (name: string) =>
    parts.find((child) => isPart(child, name)) as ReactElement | undefined;
  return {
    columns: parts.filter((child) => isPart(child, COLUMN_PART)) as ReactElement<
      HubTableColumnProps<unknown>
    >[],
    empty: slot(EMPTY_PART),
    loading: slot(LOADING_PART),
    error: slot(ERROR_PART),
    forbidden: slot(FORBIDDEN_PART),
  };
}

/**
 * O overlay de loading desconta o `thead` real (pode ter duas linhas) para centrar o spinner no corpo;
 * o `pt.loadingOverlay` da tela é mantido.
 */
function withOverlayInset(
  pt: DataTableProps<DataTableValueArray>["pt"],
  headerHeight: number,
): DataTableProps<DataTableValueArray>["pt"] {
  const own = pt?.loadingOverlay as unknown;
  return {
    ...pt,
    loadingOverlay: (options: unknown) => {
      const ownAttrs = (typeof own === "function" ? own(options) : own) as
        { style?: object } | undefined;
      return { ...ownAttrs, style: { ...ownAttrs?.style, paddingTop: headerHeight } };
    },
  } as DataTableProps<DataTableValueArray>["pt"];
}

/** O `pt.wrapper` da tela (objeto ou função) é mantido; as classes do DS (barra nativa oculta, `fit`) somam-se à dela. */
function withScrollerClass(
  pt: DataTableProps<DataTableValueArray>["pt"],
  fitClass: string | undefined,
): DataTableProps<DataTableValueArray>["pt"] {
  const own = pt?.wrapper as unknown;
  return {
    ...pt,
    wrapper: (options: unknown) => {
      const ownAttrs = (typeof own === "function" ? own(options) : own) as
        { className?: string } | undefined;
      return {
        ...ownAttrs,
        className: joinClassNames(ownAttrs?.className, styles.scroller, fitClass),
      };
    },
  } as DataTableProps<DataTableValueArray>["pt"];
}

/**
 * ViewModel do HubTable: qual estado mostrar, as colunas do Prime já filtradas por visibilidade, as props do
 * `DataTable` `lazy` e a ligação das barras de rolagem da calha com a área que rola; sem JSX de layout.
 */
export function useHubTable<T, C extends string, F extends string>(
  options: UseHubTableOptions<T, C, F> & HubTableRootAttributes<T, C, F>,
  children: ReactNode,
): HubTableViewModel {
  const {
    value,
    totalRecords,
    table,
    settings,
    status = "success",
    loadingView = "skeleton",
    emptyView = "table",
    fit,
    loadingLabel = LOADING_LABEL,
    errorMessage,
    dataKey,
    selectionMode,
    selection,
    onSelectionChange,
    onRowClick,
    rowClassName,
    emptyNoun,
    emptyGender,
    emptyPlural,
    hasSearch,
    hasFilters,
    onClear,
    onRetry,
    rowHover,
    striped,
    dataTableProps,
    paginatorProps,
    testId,
    className,
    style,
    classNames,
    ...rest
  } = options;
  const slots = splitChildren(children);
  // Escopa a tooltip dos cabeçalhos a esta tabela: com duas HubTables na página, cada `Tooltip` alcança só os
  // seus alvos.
  const tableId = useId();
  const partTestId = (part: string) => (testId ? `${testId}-${part}` : undefined);

  const tableRef = useRef<HTMLDivElement | null>(null);
  const verticalGutterRef = useRef<HTMLDivElement | null>(null);
  const [scroller, setScroller] = useState<HTMLElement | null>(null);
  // Ref nova a cada wrapper novo: o `ScrollBar` refaz a ligação quando a identidade de `scrollerRef` muda.
  const scrollerRef = useMemo(() => ({ current: scroller }), [scroller]);

  const [scrollFixed, setScrollFixed] = useState<HubTableScrollFixed>(NO_FIXED);

  // A calha vertical tem a faixa do cabeçalho (do topo da tabela, que é o da linha dela na grade da moldura, ao fim do
  // cabeçalho) e a barra ao lado do corpo (até o fim da área que rola): a medida vai direto no elemento, sem
  // render. O que fica parado (cabeçalho e colunas congeladas) vira estado: muda pouco (densidade, colunas, janela) e
  // limita a horizontal e o polegar das duas barras.
  useLayoutEffect(() => {
    const tableElement = tableRef.current;
    if (!scroller || !tableElement) return;
    const paint = () => {
      const fixed = measureFixed(scroller);
      const tableTop = tableElement.getBoundingClientRect().top;
      const scrollerBox = scroller.getBoundingClientRect();
      const bodyTop = fixed.header ? fixed.header.getBoundingClientRect().bottom : scrollerBox.top;
      const gutter = verticalGutterRef.current;
      if (gutter) {
        const head = Math.max(0, bodyTop - tableTop);
        const body = Math.max(0, scrollerBox.bottom - bodyTop);
        gutter.style.gridTemplateRows = `${head}px ${body}px`;
      }
      const next = { header: fixed.headerHeight, frozenStart: fixed.start, frozenEnd: fixed.end };
      setScrollFixed((current) =>
        current.header === next.header &&
        current.frozenStart === next.frozenStart &&
        current.frozenEnd === next.frozenEnd
          ? current
          : next,
      );
      return fixed;
    };
    const fixed = paint();
    if (typeof ResizeObserver === "undefined") return;
    const observer = new ResizeObserver(() => paint());
    for (const element of [tableElement, scroller, fixed.header, ...fixed.frozenCells]) {
      if (element) observer.observe(element);
    }
    return () => observer.disconnect();
  }, [scroller]);

  // A horizontal fica só sob a faixa que rola, entre as colunas congeladas de cada ponta.
  const scrollAxes = useMemo<HubTableViewModel["scrollAxes"]>(
    () => ({
      vertical: { inset: scrollFixed.header, style: undefined },
      horizontal: {
        inset: scrollFixed.frozenStart + scrollFixed.frozenEnd,
        style: { marginLeft: scrollFixed.frozenStart, marginRight: scrollFixed.frozenEnd },
      },
    }),
    [scrollFixed],
  );

  const visibleColumns = slots.columns.filter(
    (column) =>
      !column.props.hidden && (settings ? settings.isColumnVisible(column.props.field as C) : true),
  );

  const isLoading = status === "loading";
  const noRows = value.length === 0;
  // `emptyView="replace"`: o vazio já mostrado dá lugar ao `Loading` do DS, centrado, enquanto uma nova consulta carrega
  // (busca, filtro, "Limpar"), sem piscar a tabela com cabeçalho e overlay. A 1ª carga (nunca houve vazio) continua com a tabela e o overlay.
  // Estado ajustado na renderização (padrão do React): só carregando sem linhas ele se mantém; linhas ou vazio o
  // redefinem.
  // `armed` guarda o último vazio mostrado (textos, flags, `onClear` e o `Empty` próprio da tela): "Limpar" zera
  // `hasSearch`/`hasFilters` e troca o substantivo antes da lista voltar, e o texto "nenhum cadastrado" piscaria. Só
  // uma mudança nos valores escalares (ou nas props não funcionais do `Empty`) o refaz, para o estado não girar com
  // `onClear` e o elemento recriados a cada render.
  const emptyProps = {
    noun: emptyNoun ?? "registro",
    gender: emptyGender ?? "m",
    plural: emptyPlural,
    hasSearch: hasSearch ?? false,
    hasFilters: hasFilters ?? false,
  };
  const emptyKey = JSON.stringify(emptyProps);
  const [armed, setArmed] = useState<{
    key: string;
    empty: typeof emptyProps;
    onClear: typeof onClear;
    slot: ReactElement | undefined;
    /** Já passou por um carregamento depois de armado: só então um novo sucesso vazio é outro vazio. */
    sawLoading: boolean;
  } | null>(null);
  const wasEmpty = armed !== null;
  // Só uma consulta concluída com sucesso e sem linhas arma o vazio; erro, acesso negado e `idle` o zeram. Ao mudar a
  // busca ou os filtros o Model ainda entrega um render "sucesso e vazio" antes de virar `loading`: sem ter passado por
  // um carregamento depois de armado, esse sucesso é o antigo e o vazio armado segue (senão "Limpar" piscaria o
  // "nenhum cadastrado" ao vivo).
  let nextArmed: typeof armed = null;
  if (emptyView === "replace") {
    if (isLoading && noRows)
      nextArmed = armed && !armed.sawLoading ? { ...armed, sawLoading: true } : armed;
    else if (status === "success" && noRows) {
      const keep =
        armed &&
        (!armed.sawLoading || (armed.key === emptyKey && sameContent(armed.slot, slots.empty)));
      nextArmed = keep
        ? armed
        : { key: emptyKey, empty: emptyProps, onClear, slot: slots.empty, sawLoading: false };
    }
  }
  if (nextArmed !== armed) setArmed(nextArmed);
  const frozen = nextArmed;

  const view: HubTableView =
    status === "forbidden"
      ? "forbidden"
      : status === "error"
        ? "error"
        : isLoading && noRows && loadingView === "skeleton" && !wasEmpty
          ? "loading"
          : emptyView === "replace" && noRows && (status === "success" || (isLoading && wasEmpty))
            ? "empty"
            : "table";

  const size = settings?.primeSize ?? "normal";
  const tableKey = `${size}:${visibleColumns
    .map(({ props }) => `${props.columnKey ?? props.field}${props.frozen ? "*" : ""}`)
    .join("|")}`;

  const headerHeight = useHeaderHeight(tableRef, `${view}:${tableKey}`);

  // O wrapper muda quando a tabela entra na tela e quando o `DataTable` remonta (`key` com colunas e densidade).
  useLayoutEffect(() => {
    const found = tableRef.current?.querySelector<HTMLElement>(SCROLLER_SELECTOR) ?? null;
    setScroller((current) => (current === found ? current : found));
  }, [view, tableKey]);

  // `selectionMode` e `onRowClick` entram condicionalmente: o tipo união do Prime não aceita o objeto parcial sem o cast.
  // Padrões do `DataTable` (ícones, mensagem vazia) vêm do wrapper; aqui o padrão do Hub, as props do Prime da tela e,
  // por último, o controle do DS.
  const tableProps = {
    removableSort: true,
    ...dataTableProps,
    // `fit="content"`: o layout em coluna do Prime (`scrollHeight="flex"`) deixa a área que rola encolher até a moldura.
    ...(fit === "content"
      ? {
          scrollable: true,
          scrollHeight: "flex",
          className: joinClassNames(dataTableProps?.className, styles.fitTable),
        }
      : {}),
    value: value as DataTableValueArray,
    dataKey,
    size: settings?.primeSize ?? "normal",
    lazy: true,
    totalRecords,
    first: table.first,
    rows: table.rows,
    sortField: table.sortField,
    sortOrder: table.sortOrder,
    onSort: table.onSort,
    // Carregando com a tabela na tela (nova página/ordem, ou `loadingView="overlay"`): o Prime desenha o overlay.
    loading: isLoading,
    rowHover,
    stripedRows: striped,
    ...(selectionMode
      ? {
          selectionMode,
          selection,
          onSelectionChange: (
            event:
              | DataTableSelectionSingleChangeEvent<DataTableValueArray>
              | DataTableSelectionMultipleChangeEvent<DataTableValueArray>,
          ) => onSelectionChange?.(event.value as T | T[] | null),
        }
      : {}),
    ...(onRowClick
      ? {
          ...clickableRow<T>(onRowClick),
          // Linhas focáveis: Enter/Espaço (tratados em `onTableKeyDown`) fazem o mesmo que o clique.
          pt: {
            ...dataTableProps?.pt,
            // O `pt.bodyRow` da tela (objeto ou função) é mantido; `tabIndex` e `data-row-index` são do DS e vencem.
            bodyRow: (options: { context?: { index?: number } }) => {
              const own = dataTableProps?.pt?.bodyRow as unknown;
              const ownAttrs = typeof own === "function" ? own(options) : own;
              return {
                ...(ownAttrs as object | undefined),
                tabIndex: 0,
                "data-row-index": options.context?.index,
              };
            },
          },
        }
      : {}),
    ...(onRowClick || rowClassName
      ? {
          rowClassName: (row: unknown) =>
            [onRowClick ? CLICKABLE_ROW_CLASS : undefined, rowClassName?.(row as T)]
              .filter(Boolean)
              .join(" "),
        }
      : {}),
  } as DataTableProps<DataTableValueArray>;
  // A barra nativa some na área que rola: quem desenha a barra é a calha (`ScrollBar` do DS).
  tableProps.pt = withScrollerClass(
    tableProps.pt,
    fit === "content" ? styles.fitScroller : undefined,
  );

  /** Enter ou Espaço numa linha focada (não nos controles dentro dela) ativa `onRowClick`. */
  const onTableKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if (!onRowClick || (event.key !== "Enter" && event.key !== " ")) return;
    const row = event.target as HTMLElement;
    // `data-row-index` vem do `pt.bodyRow` acima; só a própria linha (não um controle dentro dela) ativa.
    if (row.tagName !== "TR" || row.dataset.rowIndex === undefined) return;
    const data = value[Number(row.dataset.rowIndex)];
    if (data === undefined) return;
    event.preventDefault();
    onRowClick(data);
  };

  return {
    view,
    slots: frozen?.slot ? { ...slots, empty: frozen.slot } : slots,
    testId,
    className,
    style,
    classNames:
      fit === "content"
        ? { ...classNames, table: joinClassNames(styles.fitBody, classNames?.table) }
        : (classNames ?? {}),
    rest,
    table: table as HubTableViewModel["table"],
    totalRecords,
    showPaginator: totalRecords > 0,
    paginatorProps,
    errorMessage: errorMessage ?? undefined,
    onRetry,
    onClear: frozen ? frozen.onClear : onClear,
    empty: frozen ? frozen.empty : emptyProps,
    columns: visibleColumns.map(({ props }) => {
      const { hidden, width, headerTooltip, sortable = true, ...column } = props;
      void hidden;
      const key = props.columnKey ?? props.field;
      const { header } = column;
      return {
        key,
        tooltip:
          headerTooltip && typeof header !== "function"
            ? { text: headerTooltip, label: header, testId: partTestId(`th-${key}`) }
            : undefined,
        props: { ...column, sortable, ...(width ? { style: { width, ...column.style } } : {}) },
      };
    }),
    visibleColumnCount: visibleColumns.length,
    tableKey,
    tableId,
    headerTooltipTarget: `[data-hubtable-id="${tableId}"] .${styles.headerTip}`,
    hasHeaderTooltip: visibleColumns.some(({ props }) => props.headerTooltip),
    loadingLabel,
    isLoading,
    announceLoading: isLoading && view === "table" && loadingView === "overlay",
    tableProps:
      headerHeight === undefined
        ? tableProps
        : { ...tableProps, pt: withOverlayInset(tableProps.pt, headerHeight) },
    onTableKeyDown,
    onPageChange: callAll(paginatorProps?.onPageChange, table.onPage),
    size,
    tableRef,
    verticalGutterRef,
    scrollerRef,
    scrollAxes,
    partTestId,
  };
}
